import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { formatPrice, type Locale, type Product, type ProductKind } from '@diana/shared'
import { NewsletterForm } from '../../components/NewsletterForm.js'
import { SectionHead } from '../../components/Section.js'
import { useContent } from '../../hooks/useContent.js'
import type { ContactSettings } from '../../lib/api.js'
import { useLocale, usePath } from '../../i18n/index.js'

/**
 * Products — courses, the bundle, free materials. All of it comes from the
 * database and Diana edits it in /studio → Produkty.
 *
 * There is no checkout yet (decision log: payments are a placeholder). A
 * "Kup kurs" button goes to the link Diana set on the product, else to her
 * Telegram, else to the contact page — the sale happens in conversation.
 */

type Strings = {
  loading: string
  eyebrow: string
  heading: ReactNode
  lead: string
  coursesEyebrow: string
  coursesTitle: ReactNode
  coursesIntro: string
  freeEyebrow: string
  freeTitle: ReactNode
  freeIntro: string
  empty: string
  kind: Record<ProductKind, string>
  cta: Record<ProductKind, string>
  free: string
  level: string
  duration: string
  bestPrice: string
  save: (amount: string) => string
  ctaHeading: ReactNode
  ctaBody: string
  ctaButton: string
}

const T: Record<Locale, Strings> = {
  pl: {
    loading: 'Wczytuję…',
    eyebrow: 'Produkty',
    heading: (
      <>
        Kursy i materiały — <em className="not-italic text-gold">w Twoim tempie</em>
      </>
    ),
    lead: 'Kursy online z moim wsparciem przez cały czas nauki, i bezpłatne materiały, od których możesz zacząć już dziś.',
    coursesEyebrow: 'Kursy',
    coursesTitle: (
      <>
        Trzy miesiące, <em className="not-italic text-gold">jeden poziom wyżej</em>
      </>
    ),
    coursesIntro: 'Każdy kurs to dostęp do materiałów i mój kontakt przez cały okres nauki — nie zostajesz z plikami sam.',
    freeEyebrow: 'Za darmo',
    freeTitle: (
      <>
        Zacznij <em className="not-italic text-gold">bez płacenia</em>
      </>
    ),
    freeIntro: 'Bezpłatne materiały do samodzielnej nauki. Odbierz i sprawdź, jak uczę.',
    empty: 'Brak produktów. Dodaj je w panelu → Produkty.',
    kind: { course: 'Kurs', bundle: 'Pakiet', free: 'Bezpłatny materiał' },
    cta: { course: 'Kup kurs', bundle: 'Kup pakiet', free: 'Odbierz za darmo' },
    free: 'Za darmo',
    level: 'Poziom',
    duration: 'Czas trwania',
    bestPrice: 'Najlepsza cena',
    save: (amount) => `Oszczędzasz ${amount}`,
    ctaHeading: (
      <>
        Wolisz uczyć się <em className="not-italic text-gold">ze mną</em>?
      </>
    ),
    ctaBody: 'Materiały pomagają, ale nikt nie nauczył się mówić, czytając. Zacznij od rozmowy.',
    ctaButton: 'Umów bezpłatną rozmowę',
  },
  uk: {
    loading: 'Завантажую…',
    eyebrow: 'Матеріали',
    heading: (
      <>
        Курси й матеріали — <em className="not-italic text-gold">у твоєму темпі</em>
      </>
    ),
    lead: 'Онлайн-курси з моєю підтримкою протягом усього навчання і безкоштовні матеріали, з яких можна почати вже сьогодні.',
    coursesEyebrow: 'Курси',
    coursesTitle: (
      <>
        Три місяці — <em className="not-italic text-gold">на рівень вище</em>
      </>
    ),
    coursesIntro: 'Кожен курс — це доступ до матеріалів і зв’язок зі мною протягом усього навчання. Ти не залишаєшся сам на сам із файлами.',
    freeEyebrow: 'Безкоштовно',
    freeTitle: (
      <>
        Почни <em className="not-italic text-gold">без оплати</em>
      </>
    ),
    freeIntro: 'Безкоштовні матеріали для самостійного навчання. Забери і подивися, як я навчаю.',
    empty: 'Немає продуктів. Додай їх у панелі → Продукти.',
    kind: { course: 'Курс', bundle: 'Пакет', free: 'Безкоштовний матеріал' },
    cta: { course: 'Купити курс', bundle: 'Купити пакет', free: 'Отримати безкоштовно' },
    free: 'Безкоштовно',
    level: 'Рівень',
    duration: 'Тривалість',
    bestPrice: 'Найкраща ціна',
    save: (amount) => `Економія ${amount}`,
    ctaHeading: (
      <>
        Хочеш вчитися <em className="not-italic text-gold">зі мною</em>?
      </>
    ),
    ctaBody: 'Матеріали допомагають, але ще ніхто не навчився говорити, читаючи. Почни з розмови.',
    ctaButton: 'Записатися на безкоштовну розмову',
  },
  ru: {
    loading: 'Загружаю…',
    eyebrow: 'Материалы',
    heading: (
      <>
        Курсы и материалы — <em className="not-italic text-gold">в твоём темпе</em>
      </>
    ),
    lead: 'Онлайн-курсы с моей поддержкой на протяжении всего обучения и бесплатные материалы, с которых можно начать уже сегодня.',
    coursesEyebrow: 'Курсы',
    coursesTitle: (
      <>
        Три месяца — <em className="not-italic text-gold">на уровень выше</em>
      </>
    ),
    coursesIntro: 'Каждый курс — это доступ к материалам и связь со мной на протяжении всего обучения. Ты не остаёшься один на один с файлами.',
    freeEyebrow: 'Бесплатно',
    freeTitle: (
      <>
        Начни <em className="not-italic text-gold">без оплаты</em>
      </>
    ),
    freeIntro: 'Бесплатные материалы для самостоятельного изучения. Забери и посмотри, как я учу.',
    empty: 'Нет продуктов. Добавь их в панели → Продукты.',
    kind: { course: 'Курс', bundle: 'Пакет', free: 'Бесплатный материал' },
    cta: { course: 'Купить курс', bundle: 'Купить пакет', free: 'Получить бесплатно' },
    free: 'Бесплатно',
    level: 'Уровень',
    duration: 'Длительность',
    bestPrice: 'Лучшая цена',
    save: (amount) => `Экономия ${amount}`,
    ctaHeading: (
      <>
        Хочешь учиться <em className="not-italic text-gold">со мной</em>?
      </>
    ),
    ctaBody: 'Материалы помогают, но говорить, читая, ещё никто не научился. Начни с разговора.',
    ctaButton: 'Записаться на бесплатную встречу',
  },
}

export function ProductsPage() {
  const locale = useLocale()
  const s = T[locale]
  const to = usePath()
  const { data, isLoading } = useContent(locale)

  if (isLoading || !data) {
    return <div className="mx-auto max-w-6xl px-5 py-32 font-light">{s.loading}</div>
  }

  const telegram = ((data.settings.contact as ContactSettings | undefined)?.telegram ?? '').trim()
  const paid = data.products.filter((p) => p.kind !== 'free')
  const free = data.products.filter((p) => p.kind === 'free')

  const card = (p: Product) => (
    <ProductCard key={p.id} product={p} s={s} telegram={telegram} contactPath={to('/kontakt')} />
  )

  return (
    <>
      <section className="mx-auto max-w-6xl px-5 pb-4 pt-16 lg:px-8 lg:pt-24">
        <p className="eyebrow">{s.eyebrow}</p>
        <h1 className="h-display mt-5 max-w-3xl text-[2.6rem] leading-[1.1] sm:text-5xl">{s.heading}</h1>
        <div className="rule-gold mt-8 w-28" />
        <p className="lead mt-8 max-w-2xl">{s.lead}</p>
      </section>

      {data.products.length === 0 && (
        <section className="mx-auto max-w-6xl px-5 py-20 lg:px-8">
          <p className="border border-dashed border-sky-300 p-14 text-center font-light">{s.empty}</p>
        </section>
      )}

      {/* ── courses ───────────────────────────────────────────────────── */}
      {paid.length > 0 && (
        <section className="py-20 lg:py-24">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <SectionHead eyebrow={s.coursesEyebrow} title={s.coursesTitle} intro={s.coursesIntro} />
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{paid.map(card)}</div>
          </div>
        </section>
      )}

      {/* ── free ──────────────────────────────────────────────────────── */}
      {free.length > 0 && (
        <section className="bg-ivory-deep py-24 lg:py-28">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <SectionHead eyebrow={s.freeEyebrow} title={s.freeTitle} intro={s.freeIntro} />
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{free.map(card)}</div>
          </div>
        </section>
      )}

      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <NewsletterForm />
        </div>
      </section>

      <section className="bg-ink py-20 text-center lg:py-24">
        <div className="mx-auto max-w-2xl px-5">
          <h2 className="h-display text-3xl leading-tight text-ivory sm:text-4xl">{s.ctaHeading}</h2>
          <p className="mx-auto mt-6 max-w-lg leading-relaxed font-light text-sky-200/80">{s.ctaBody}</p>
          <Link
            to={to('/rezerwacja')}
            className="btn mt-9 border border-gold-300 bg-gold-300 text-ink hover:bg-transparent hover:text-gold-300"
          >
            {s.ctaButton}
          </Link>
        </div>
      </section>
    </>
  )
}

function ProductCard({
  product: p,
  s,
  telegram,
  contactPath,
}: {
  product: Product
  s: Strings
  telegram: string
  contactPath: string
}) {
  const isFree = p.kind === 'free' || p.priceGrosz === 0
  const saving = p.compareAtGrosz > p.priceGrosz ? p.compareAtGrosz - p.priceGrosz : 0
  const meta = [p.level && `${s.level}: ${p.level}`, p.duration && `${s.duration}: ${p.duration}`].filter(Boolean)

  return (
    <article className={`relative flex flex-col p-7 ${p.featured ? 'card-feature' : 'card'}`}>
      {p.coverUrl && (
        <div className="-mx-7 -mt-7 mb-6 aspect-[16/10] overflow-hidden rounded-t-[0.25rem] border-b border-sky-200">
          <img src={p.coverUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
        </div>
      )}

      {p.featured && (
        <span className="absolute -top-3 left-7 bg-gold px-3 py-1 font-roboto text-[0.6rem] font-bold uppercase tracking-[0.18em] text-porcelain">
          {p.badge || s.bestPrice}
        </span>
      )}

      <p className="font-roboto text-[0.65rem] font-bold uppercase tracking-[0.2em] text-gold-600">{s.kind[p.kind]}</p>
      <h3 className="h-display mt-3 text-xl leading-snug text-ink">{p.title}</h3>
      {p.subtitle && <p className="h-display mt-1.5 text-sm italic text-gold-600">{p.subtitle}</p>}

      <div className="mt-6">
        {/* Gold on the numeral only — the one place it always looks expensive. */}
        <span className="h-display text-[2.5rem] leading-none text-gold">
          {isFree ? s.free : formatPrice(p.priceGrosz)}
        </span>
        {saving > 0 && (
          <span className="ml-3 font-roboto text-sm text-ink-faint line-through">{formatPrice(p.compareAtGrosz)}</span>
        )}
        {saving > 0 && (
          <p className="mt-2 font-roboto text-[0.65rem] font-bold uppercase tracking-[0.18em] text-gold-600">
            {s.save(formatPrice(saving))}
          </p>
        )}
      </div>

      {meta.length > 0 && (
        <p className="mt-2 font-roboto text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint">{meta.join(' · ')}</p>
      )}

      <div className="rule-gold mt-6 w-full" />

      {p.description && <p className="mt-5 text-[0.9rem] leading-relaxed text-ink-soft">{p.description}</p>}

      {p.includes.length > 0 && (
        <ul className="mt-5 space-y-2.5">
          {p.includes.map((item) => (
            <li key={item} className="flex gap-3 text-[0.85rem] leading-relaxed font-light">
              <span aria-hidden className="mt-2.5 h-px w-3 shrink-0 bg-gold-300" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-auto pt-8">
        <ProductButton href={p.ctaUrl || telegram || contactPath} label={p.ctaLabel || s.cta[p.kind]} featured={p.featured} />
      </div>
    </article>
  )
}

function ProductButton({ href, label, featured }: { href: string; label: string; featured: boolean }) {
  const className = `btn btn-sm w-full ${featured ? 'btn-gold' : 'btn-outline'}`
  if (/^https?:\/\//.test(href)) {
    return (
      <a href={href} target="_blank" rel="noreferrer noopener" className={className}>
        {label}
      </a>
    )
  }
  return (
    <Link to={href} className={className}>
      {label}
    </Link>
  )
}
